import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaFileExcel, FaSearch, FaFilter } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import pythonApiClient from '../../../services/api/pythonBaseApi';

const ReporteAsignaciones = () => {
    const [asignaciones, setAsignaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filtros, setFiltros] = useState({
        fecha_inicio: '',
        fecha_fin: '',
        estado_asignacion: '',
        trabajador_busqueda: '' // Para input visual
    });

    // Obtener ID del proyecto seleccionado
    const getSelectedProject = () => {
        try {
            const project = localStorage.getItem('selectedProject');
            return project ? JSON.parse(project) : null;
        } catch (error) {
            console.error("Error parsing selectedProject:", error);
            return null;
        }
    };

    const selectedProject = getSelectedProject();

    const loadReporte = async () => {
        if (!selectedProject?.id) return;

        setLoading(true);
        setError(null);
        try {
            // Preparar parámetros
            const params = {
                proyecto_id: selectedProject.id
            };

            if (filtros.fecha_inicio) params.fecha_inicio = filtros.fecha_inicio;
            if (filtros.fecha_fin) params.fecha_fin = filtros.fecha_fin;
            if (filtros.estado_asignacion) params.estado = filtros.estado_asignacion;

            // Nota: La búsqueda por trabajador requeriría primero buscar el trabajador para obtener su ID
            // Por simplicidad, aquí filtraremos en cliente si es necesario o implementaremos búsqueda en backend luego

            const response = await pythonApiClient.get('/reportes/hoteleria/ocupabilidad/', { params });

            if (response.data.success) {
                let data = response.data.data;
                // Filtrado client-side temporal para búsqueda de texto
                if (filtros.trabajador_busqueda) {
                    const term = filtros.trabajador_busqueda.toLowerCase();
                    data = data.filter(item =>
                        item.trabajador_nombre.toLowerCase().includes(term) ||
                        item.trabajador_documento.includes(term)
                    );
                }
                setAsignaciones(data);
            } else {
                setError(response.data.message || 'No se pudo cargar el reporte');
            }
        } catch (error) {
            console.error("Error cargando reporte:", error);
            setError('No se pudo conectar con el servidor');
        } finally {
            setLoading(false);
        }
    };

    // Cargar al inicio
    useEffect(() => {
        if (selectedProject?.id) {
            loadReporte();
        }
    }, [selectedProject?.id]);

    const handleFilterChange = (field, value) => {
        setFiltros(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        loadReporte();
    };

    const exportToExcel = () => {
        const dataToExport = asignaciones.map(item => ({
            'Trabajador': item.trabajador_nombre,
            'Documento': item.trabajador_documento,
            'Cama': item.cama_codigo,
            'Habitación': item.habitacion_numero,
            'Módulo': item.modulo_nombre,
            'Zona': item.zona_nombre,
            'Tipo Habitación': item.tipo_habitacion,
            'Fecha Reserva': item.fecha_reserva ? new Date(item.fecha_reserva).toLocaleString() : '-',
            'Fecha Check-in': item.fecha_check_in ? new Date(item.fecha_check_in).toLocaleString() : '-',
            'Fecha Check-out': item.fecha_check_out ? new Date(item.fecha_check_out).toLocaleString() : '-',
            'Estado Asignación': item.estado_asignacion,
            'Estado Cama Actual': item.estado_cama_actual,
            'Observaciones': item.observaciones || ''
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Asignaciones");
        XLSX.writeFile(wb, "Reporte_Asignaciones_Camas.xlsx");
    };

    const getEstadoBadgeVariant = (estado) => {
        switch (estado?.toLowerCase()) {
            case 'activa': return 'success';
            case 'liberada': return 'primary';
            case 'cancelada': return 'danger';
            default: return 'secondary';
        }
    };

    if (!selectedProject) {
        return (
            <Container className="p-5">
                <Alert variant="warning">Por favor seleccione un proyecto para ver el reporte.</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Card className="shadow-sm">
                <Card.Header className="bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">Reporte de Ocupabilidad</h4>
                        <Button
                            variant="success"
                            onClick={exportToExcel}
                            disabled={loading}
                        >
                            <FaFileExcel className="me-2" />
                            Exportar Excel ({asignaciones.length} registros)
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* Mensajes de error */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}

                    <Alert variant="info">
                        <strong>📊 Reporte de Ocupabilidad de Camas</strong>
                        <p className="mb-0 mt-2">
                            Este reporte muestra todas las asignaciones de camas activas (reservadas y ocupadas) del proyecto.
                            Use el botón <strong>Exportar Excel</strong> para descargar el detalle completo.
                        </p>
                    </Alert>

                    {asignaciones.length === 0 && !loading && !error && (
                        <Alert variant="warning">
                            No hay asignaciones activas en el proyecto actual. 
                            El Excel se generará vacío si no hay datos.
                        </Alert>
                    )}

                    {asignaciones.length > 0 && (
                        <Alert variant="success">
                            ✅ Se encontraron <strong>{asignaciones.length}</strong> asignaciones activas listas para exportar.
                        </Alert>
                    )}

                    {loading && (
                        <div className="text-center p-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Generando datos para el reporte de ocupabilidad...</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ReporteAsignaciones;
